/**
 * Workspace Service — Server-backed workspace CRUD and hydration
 *
 * All workspace state is persisted to the backend. No sessionStorage.
 * Endpoints match docs/plans/api-contracts.md workspace section.
 */

import api from './api'
import type { MedsStatus } from '../components/patient-panel/medsStatus'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type WorkspaceType = 'admission' | 'flow' | 'chat'

/** Lightweight workspace summary for sidebar listing */
export interface WorkspaceSummary {
  id: string
  type: WorkspaceType
  display_name: string
  active_phase: string | null
  is_closed: boolean
  updated_at: string
}

/** Full workspace detail */
export interface WorkspaceDetail {
  id: string
  type: WorkspaceType
  display_name: string
  active_phase: string | null
  active_agent: string | null
  ui_state: Record<string, unknown>
  is_closed: boolean
  created_at: string
  updated_at: string
}

/** Fields that can be updated via PUT */
export interface WorkspaceUpdate {
  display_name: string
  active_phase: string | null
  active_agent: string | null
  ui_state: Record<string, unknown>
}

/** Hydration response — full workspace state for restoring a session */
interface HydratedMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  metadata: Record<string, unknown>
  created_at: string
  thinking_content?: string | null
  thinking_duration_ms?: number | null
}

export interface HydratedConversation {
  id: string
  agent_id: string
  title: string
  message_count: number
  last_message_at: string
  messages: HydratedMessage[]
}

export interface HydratedArtifact {
  id: string
  type: string
  title: string
  current_content: string
  current_version: number
  source: string
  created_at: string
  updated_at: string
}

export interface HydratedClinicalData {
  id: string
  patient_summary: string
  allergies: string[]
  patient_age: number | null
  patient_sex: string | null
  extractions: unknown[]
  /** @deprecated Legacy blob — replaced by structured summary_presentation/summary_admission_context. Remove once old workspaces are migrated. */
  panel_content: string | null
  updated_at: string | null
  // Structured clinical sections
  vitals: VitalSignData[] | null
  labs: LabGroupData[] | null
  imaging: ImagingData[] | null
  orders: OrdersData | null
  medications: MedicationsData | null
  summary_presentation: string | null
  summary_admission_context: string | null
  med_recon_status: MedsStatus
}

// Structured data shapes from backend JSONB
export interface VitalSignData {
  label: string
  value: string
  unit: string
  ref_low: number | null
  ref_high: number | null
  ref_range: string | null
}

export interface LabTestData {
  name: string
  value: string
  unit: string
  ref_range: string
  flag: 'high' | 'low' | null
}

export interface LabGroupData {
  group: string
  tests: LabTestData[]
}

export interface ImagingData {
  modality: string
  finding: string
}

export interface OrderSectionData {
  title: string
  items: string[]
}

export interface OrdersData {
  sections: OrderSectionData[]
}

export interface MedicationsData {
  continue: string[]
  hold: string[]
  adjust: string[]
  discontinue: string[]
  drug_interactions: string[]
  prophylaxis_gaps: string[]
}

export interface HydratedFile {
  id: string
  filename: string
  content_type: string | null
  file_size: number | null
  content_category: string
  status: string
  created_at: string | null
}

export interface HydratedNotification {
  id: string
  type: 'agent_activity' | 'clinical_alert' | 'content_change'
  message: string
  read: boolean
  timestamp: string
}

export interface HydrationResponse {
  workspace: WorkspaceDetail
  conversations: HydratedConversation[]
  artifacts: HydratedArtifact[]
  clinical_data: HydratedClinicalData | null
  files: HydratedFile[]
  notifications: HydratedNotification[]
}

type IntakeStartStatus =
  | 'started'
  | 'already_running'
  | 'already_complete'
  | 'restarted'
  | 'not_eligible'

interface IntakeStartResponse {
  status: IntakeStartStatus
  active_phase: string | null
  started_at: string | null
  // Backend returns a partial snapshot here (id, active_phase, display_name,
  // is_closed, ui_state) — NOT a full WorkspaceDetail. Typed as Partial so a
  // future caller can't dereference an absent field (type/created_at/...) and
  // get undefined at runtime with no compile error.
  workspace: Partial<WorkspaceDetail>
  reason: string | null
}

// ────────────────────────────────────────────────────────
// Service
// ────────────────────────────────────────────────────────

export const workspaceService = {
  /** Create a new workspace */
  create: async (type: WorkspaceType, displayName?: string): Promise<WorkspaceDetail> => {
    const response = await api.post<WorkspaceDetail>('/api/v1/workspaces', {
      type,
      display_name: displayName,
    })
    return response.data
  },

  /** List workspaces (lightweight, for sidebar) */
  list: async (includeClosed = false): Promise<WorkspaceSummary[]> => {
    const response = await api.get<{ workspaces: WorkspaceSummary[] }>(
      '/api/v1/workspaces',
      { params: { include_closed: includeClosed } }
    )
    return response.data.workspaces
  },

  /** Get full workspace detail */
  get: async (id: string): Promise<WorkspaceDetail> => {
    const response = await api.get<WorkspaceDetail>(`/api/v1/workspaces/${id}`)
    return response.data
  },

  /** Update workspace fields (partial update) */
  update: async (id: string, updates: Partial<WorkspaceUpdate>): Promise<WorkspaceDetail> => {
    const response = await api.put<WorkspaceDetail>(`/api/v1/workspaces/${id}`, updates)
    return response.data
  },

  /** Close or hard-delete a workspace */
  close: async (id: string, hard = false): Promise<{ status: string }> => {
    const response = await api.delete<{ status: string }>(
      `/api/v1/workspaces/${id}`,
      { params: { hard } }
    )
    return response.data
  },

  /** Reopen a closed workspace */
  reopen: async (id: string): Promise<WorkspaceDetail> => {
    const response = await api.patch<WorkspaceDetail>(`/api/v1/workspaces/${id}/reopen`)
    return response.data
  },

  /** Hydrate full workspace state (conversations, artifacts, clinical data) */
  hydrate: async (id: string): Promise<HydrationResponse> => {
    const response = await api.get<HydrationResponse>(`/api/v1/workspaces/${id}/hydrate`)
    return response.data
  },

  /** Fetch just clinical data for a workspace (lightweight alternative to full hydrate) */
  fetchClinicalData: async (id: string): Promise<HydratedClinicalData | null> => {
    const response = await api.get<HydratedClinicalData | null>(`/api/v1/workspaces/${id}/clinical-data`)
    return response.data
  },

  /** Fetch a single artifact by ID */
  fetchArtifact: async (artifactId: string): Promise<HydratedArtifact> => {
    const response = await api.get<HydratedArtifact>(`/api/v1/artifacts/${artifactId}`)
    return response.data
  },

  /** Kick off the intake pipeline server-side (overlay-handoff entry point) */
  startIntake: async (
    id: string,
    options: { displayName?: string } = {},
  ): Promise<IntakeStartResponse> => {
    const body = options.displayName !== undefined
      ? { display_name: options.displayName }
      : {}
    const response = await api.post<IntakeStartResponse>(
      `/api/v1/workspaces/${id}/intake/start`,
      body,
    )
    return response.data
  },
}
